import React from "react";
import Seo from "./Seo";

const PENTESTSCAN_URL = "https://pentestscan.app";
const REPORT_MAIL =
  "mailto:linuxsecops@proton.me?subject=Request%20a%20free%20security%20report%20-%20PentestScan";

const HERO_CTA_LABEL = "Run a Free Scan";

const keyFeatures = [
  {
    title: "Attack Path Analysis",
    body: "Understand how vulnerabilities connect into real attack scenarios.",
  },
  {
    title: "Context-Aware Findings",
    body: [
      "Every finding includes real context:",
      "what it means, how it can be exploited, and what actually matters.",
    ],
  },
  {
    title: "JWT Abuse Detection",
    body: "Detect suspicious authentication behavior and potential token misuse.",
  },
  {
    title: "CI/CD Security Gate",
    body: "Stop risky deployments before they reach production.",
  },
];

function FeatureBody({ body }) {
  if (Array.isArray(body)) {
    return (
      <p className="wvs-lp-feature-body">
        {body.map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 ? <br /> : null}
            {line}
          </React.Fragment>
        ))}
      </p>
    );
  }
  return <p className="wvs-lp-feature-body">{body}</p>;
}

function CtaWithMicroTrust({ large = false }) {
  return (
    <div className="wvs-lp-cta-stack">
      <a
        href={PENTESTSCAN_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={
          large
            ? "wvs-btn wvs-btn-primary wvs-btn-large wvs-lp-cta"
            : "wvs-btn wvs-btn-primary wvs-lp-cta"
        }
      >
        {HERO_CTA_LABEL}
      </a>
      <p className="wvs-lp-cta-micro">No signup required.</p>
    </div>
  );
}

function WebVulnerabilityScanner() {
  return (
    <div className="wvs-container wvs-lp">
      <Seo
        title="PentestScan - Attack paths, not just another scanner"
        description="Attack-aware security platform: real attack paths, context-rich findings, JWT checks, CI gate. 100% free. pentestscan.app"
        path="/wvs"
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "PentestScan",
          description:
            "Attack-aware security platform. Maps findings to attack paths with context, JWT abuse detection, and CI/CD security gate.",
          url: PENTESTSCAN_URL,
          operatingSystem: "Web-based",
          applicationCategory: "SecurityApplication",
          offers: {
            "@type": "Offer",
            name: "Early access",
            price: "0",
            priceCurrency: "USD",
            description: "100% free during open testing; pricing may follow.",
          },
        }}
      />

      <section className="wvs-hero wvs-lp-hero">
        <div className="wvs-hero-content wvs-lp-hero-inner">
          <h1 className="wvs-lp-product-name">PentestScan</h1>
          <span className="wvs-lp-hero-positioning">Attack-aware security platform</span>
          <p className="wvs-lp-hero-kicker">
            Understand how your app can actually be attacked - not just what&apos;s vulnerable.
          </p>
          <div className="wvs-hero-actions wvs-lp-hero-actions">
            <CtaWithMicroTrust />
          </div>
        </div>
      </section>

      <section className="wvs-section wvs-lp-slab">
        <div className="wvs-lp-narrow">
          <h2 className="wvs-lp-h2">Most vulnerability scanners create noise.</h2>
          <p className="wvs-lp-prose">
            You get dozens of findings…
            <br />
            but no clear understanding of what actually matters.
          </p>
          <ul className="wvs-lp-questions">
            <li>What can be exploited?</li>
            <li>What should you fix first?</li>
            <li>What is just noise?</li>
          </ul>
          <p className="wvs-lp-prose wvs-lp-problem-hook">
            That&apos;s where most tools fail.
          </p>
          <p className="wvs-lp-prose wvs-lp-problem-tier2">
            They show problems - but not real risk.
          </p>
          <p className="wvs-lp-prose wvs-lp-problem-tier3">
            And that&apos;s why teams miss what actually matters.
          </p>
        </div>
      </section>

      <section className="wvs-section wvs-section-gray wvs-lp-slab">
        <div className="wvs-lp-narrow">
          <h2 className="wvs-lp-h2">PentestScan changes that.</h2>
          <p className="wvs-lp-prose wvs-lp-prose-lg">
            Instead of isolated findings, it connects vulnerabilities into <strong>real attack paths</strong>.
          </p>
          <p className="wvs-lp-prose">
            So you don’t just see problems - you see <strong>how an attacker would actually use them</strong>.
          </p>
        </div>
      </section>

      <section className="wvs-section wvs-lp-slab">
        <div className="wvs-lp-narrow wvs-lp-narrow-wide">
          <h2 className="wvs-lp-h2 wvs-lp-center">What makes PentestScan different</h2>
          <div className="wvs-lp-feature-grid">
            {keyFeatures.map((f) => (
              <div key={f.title} className="wvs-lp-feature">
                <h3 className="wvs-lp-feature-title">{f.title}</h3>
                <FeatureBody body={f.body} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="wvs-section wvs-section-gray wvs-lp-slab">
        <div className="wvs-lp-narrow wvs-lp-narrow-wide">
          <h2 className="wvs-lp-h2 wvs-lp-center">Before vs after</h2>
          <h3 className="wvs-lp-compare-why">Why this matters</h3>
          <div className="wvs-lp-compare">
            <div className="wvs-lp-compare-col wvs-lp-compare-bad">
              <h3 className="wvs-lp-compare-heading">Traditional scanners:</h3>
              <ul>
                <li>❌ 50+ findings</li>
                <li>❌ no prioritization</li>
                <li>❌ no context</li>
              </ul>
            </div>
            <div className="wvs-lp-compare-col wvs-lp-compare-good">
              <h3 className="wvs-lp-compare-heading">PentestScan:</h3>
              <ul>
                <li>✔ clear attack paths</li>
                <li>✔ prioritized risk</li>
                <li>✔ actionable fixes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="wvs-section wvs-lp-slab wvs-lp-free">
        <div className="wvs-lp-narrow wvs-lp-narrow-cta">
          <h2 className="wvs-lp-h2 wvs-lp-center">Currently 100% free (limited time).</h2>
          <p className="wvs-lp-prose wvs-lp-center">
            The platform is open for real-world testing and feedback.
            <br />
            Try it on your application and see how it performs in a real scenario.
          </p>
          <div className="wvs-hero-actions wvs-lp-hero-actions">
            <CtaWithMicroTrust />
          </div>
        </div>
      </section>

      <section className="wvs-cta wvs-lp-cta-block">
        <div className="wvs-cta-content wvs-lp-cta-content">
          <h2>Want to see how your app actually looks to an attacker?</h2>
          <p>
            Run a scan or request a full security report for your application.
          </p>
          <div className="wvs-cta-actions wvs-lp-final-actions">
            <CtaWithMicroTrust large />
            <a
              href={REPORT_MAIL}
              className="wvs-btn wvs-btn-outline wvs-btn-large wvs-lp-cta-secondary-fomo"
            >
              Request a Free Report (limited availability)
            </a>
          </div>
        </div>
      </section>

      <div className="wvs-lp-trust wvs-lp-trust-bottom">
        <p className="wvs-lp-trust-line">
          Built for developers and teams who want real visibility into application security.
        </p>
        <p className="wvs-lp-trust-line">No setup required. Start scanning in minutes.</p>
        <p className="wvs-lp-trust-line wvs-lp-trust-line-bonus">
          Works with any web app or API.
        </p>
      </div>
    </div>
  );
}

export default WebVulnerabilityScanner;
